import React from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneInput } from '@/components/auth/PhoneInput';
import { FileUploadField } from './FileUploadField';
import { FormsetField } from './FormsetField';
import type { SurveyQuestion } from '@/types/survey';
import type { WeeklyScheduleSlot } from './FormsetField';
import {
  isValidRegexPattern,
  createSafeRegExp,
  getRegexValidationMessage,
  getRegexFormatHint,
} from '@/utils/validation';
import { encodeSurveyFieldName } from '@/utils/surveyFormHelpers';
import { SearchableDynamicDropdown } from './SearchableDynamicDropdown';

/**
 * react-hook-form treats dots as nested path separators.
 * Question codes like "DEM 1.00" must be escaped to avoid being
 * split into { "DEM 1": { "00": value } }.
 */
export const encodeFieldName = encodeSurveyFieldName;

interface QuestionRendererProps {
  question: SurveyQuestion;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  locale: string;
  disabled?: boolean;
  /**
   * Latest survey snapshot id — required to resolve `option_source` dropdowns.
   * Forwarded by SurveyFormRenderer.
   */
  snapshotId?: string | null;
}

/**
 * Renders a single survey question as the appropriate form control
 * based on the question type from the backend survey definition.
 */
export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  form,
  locale,
  disabled = false,
  snapshotId,
}) => {
  const isAr = locale === 'ar';
  const fieldName = encodeFieldName(question.code);

  // Watch only the depends_on answers. When this question has no
  // option_source, the watched array is empty and useWatch is a no-op.
  const dependsOnCodes = question.option_source?.depends_on?.map(
    d => d.from_question_code,
  ) ?? [];
  const dependsOnFieldNames = dependsOnCodes.map(encodeFieldName);
  // useWatch with an empty `name` array would mean "watch nothing"; useWatch
  // accepts a stable array of strings, so we pass [] and short-circuit below.
  const watchedDependencies = useWatch({
    control: form.control,
    name: dependsOnFieldNames,
  });

  const dependencyAnswers: Record<string, unknown> = {};
  if (dependsOnCodes.length > 0) {
    const values = Array.isArray(watchedDependencies)
      ? watchedDependencies
      : [watchedDependencies];
    dependsOnCodes.forEach((code, i) => {
      dependencyAnswers[code] = values[i];
    });
  }

  // When a parent answer changes (e.g. user switches city), the previously
  // chosen value of THIS question is no longer in the option set — clear it
  // so the form doesn't submit a stale id. SearchableDynamicDropdown handles
  // its own option fetching, but the form still holds the old value.
  const dependencyKey = JSON.stringify(dependencyAnswers);
  const lastDependencyKeyRef = React.useRef(dependencyKey);
  const isDynamic = !!question.option_source;
  // A cascading dynamic dropdown (e.g. neighborhood) declares depends_on so its
  // options are fetched per the chosen parent (e.g. city). We always render
  // these as multi-select, and make them required only when the chosen parent
  // actually has options (tracked via hasOptions below).
  const isCascadingDynamic =
    isDynamic && (question.option_source?.depends_on?.length ?? 0) > 0;
  // Backend uses 'multiple_choice' (the QuestionBank.question_type DB value);
  // older clients use 'multi_choice'. Treat them as the same shape. Cascading
  // dynamic dropdowns are forced multi-select regardless of the backend type.
  const isMultiDynamic =
    question.type === 'multi_choice' ||
    question.type === 'multiple_choice' ||
    isCascadingDynamic;
  // Persist the selected option's label(s) across re-fetches (the search may
  // have filtered the chosen row out of the visible page). Single-choice
  // dropdowns hold a string; multi-choice dropdowns hold a {value: label} map
  // so chips render even after server-side filtering.
  const [selectedDynamicLabel, setSelectedDynamicLabel] = React.useState<string | undefined>();
  const [selectedDynamicLabels, setSelectedDynamicLabels] = React.useState<Record<string, string>>({});
  // For cascading dynamic dropdowns: whether the chosen parent has any options.
  // Drives both the "required" rule and the asterisk on the label. A ref mirrors
  // it so the validate closure always reads the current value at submit time.
  const [hasOptions, setHasOptions] = React.useState(false);
  const hasOptionsRef = React.useRef(false);
  React.useEffect(() => {
    hasOptionsRef.current = hasOptions;
  }, [hasOptions]);

  React.useEffect(() => {
    if (!question.option_source) return;
    if (lastDependencyKeyRef.current !== dependencyKey) {
      const currentValue = form.getValues(fieldName);
      const isEmpty =
        currentValue === undefined ||
        currentValue === '' ||
        currentValue === null ||
        (Array.isArray(currentValue) && currentValue.length === 0);
      if (!isEmpty) {
        form.setValue(fieldName, isMultiDynamic ? [] : '', {
          shouldDirty: true,
          shouldValidate: false,
        });
        if (isMultiDynamic) setSelectedDynamicLabels({});
        else setSelectedDynamicLabel(undefined);
      }
      lastDependencyKeyRef.current = dependencyKey;
    }
  }, [dependencyKey, fieldName, form, question.option_source, isMultiDynamic]);
  const questionText = isAr ? (question.text_ar || question.text_en || question.text) : (question.text_en || question.text);
  const helpText = isAr ? (question.help_text_ar || question.help_text_en || question.help_text) : (question.help_text_en || question.help_text);
  const placeholder = isAr ? (question.placeholder_ar || question.placeholder_en || question.placeholder) : (question.placeholder_en || question.placeholder);

  const fieldError = form.formState.errors[fieldName];
  const errorMessage = fieldError?.message as string | undefined;

  const getOptionLabel = (option: { value: string; label?: string; label_en?: string; label_ar?: string }) => {
    if (isAr && option.label_ar) return option.label_ar;
    if (option.label_en) return option.label_en;
    return option.label || option.value;
  };

  const hasRegex = isValidRegexPattern(question.validation_regex);
  const regexPattern = hasRegex ? createSafeRegExp(question.validation_regex!) : null;
  const formatHint = hasRegex ? getRegexFormatHint(question.validation_regex!, isAr) : null;
  const regexMsg = hasRegex ? getRegexValidationMessage(question.validation_regex!, isAr) : '';
  const requiredMsg = question.required ? (isAr ? 'مطلوب' : 'Required') : false;

  const isPhoneQuestion =
    question.type === 'phone' ||
    question.code === 'CONTACT1' ||
    /mobile|phone|جوال|هاتف/i.test(question.text_en || question.text || '');

  const renderField = () => {
    if (isPhoneQuestion) {
      return (
        <Controller
          name={fieldName}
          control={form.control}
          rules={{
            required: requiredMsg,
            ...(hasRegex
              ? {
                  validate: {
                    regexPattern: (value: string) => {
                      if (!value) return true;
                      try {
                        return regexPattern!.test(value) || regexMsg;
                      } catch {
                        return true;
                      }
                    },
                  },
                }
              : {
                  minLength: {
                    value: 9,
                    message: isAr ? 'رقم الهاتف قصير جداً' : 'Phone number is too short',
                  },
                }),
          }}
          render={({ field }) => (
            <PhoneInput
              value={field.value ? String(field.value) : ''}
              onChange={field.onChange}
              error={errorMessage}
              disabled={disabled}
              hideLabel
            />
          )}
        />
      );
    }

    // Cascading dynamic dropdowns render as multi-select even when the backend
    // marks them single_choice (see isCascadingDynamic).
    const effectiveType = isCascadingDynamic ? 'multiple_choice' : question.type;

    switch (effectiveType) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <Input
            type={
              question.type === 'email'
                ? 'email'
                : question.type === 'password'
                  ? 'password'
                  : 'text'
            }
            placeholder={placeholder || ''}
            disabled={disabled}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: requiredMsg,
              ...(hasRegex
                ? { pattern: { value: regexPattern!, message: regexMsg } }
                : question.type === 'email'
                  ? {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: isAr ? 'بريد إلكتروني غير صالح' : 'Invalid email address',
                      },
                    }
                  : question.type === 'password' && question.required
                    ? {
                        minLength: {
                          value: 8,
                          message: isAr
                            ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل'
                            : 'Password must be at least 8 characters',
                        },
                        validate: {
                          uppercase: (v: string) =>
                            /[A-Z]/.test(v) ||
                            (isAr ? 'يجب أن تحتوي على حرف كبير' : 'Must contain an uppercase letter'),
                          lowercase: (v: string) =>
                            /[a-z]/.test(v) ||
                            (isAr ? 'يجب أن تحتوي على حرف صغير' : 'Must contain a lowercase letter'),
                          number: (v: string) =>
                            /[0-9]/.test(v) || (isAr ? 'يجب أن تحتوي على رقم' : 'Must contain a number'),
                        },
                      }
                    : {}),
            })}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={placeholder || ''}
            disabled={disabled}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: false,
              valueAsNumber: true,
              validate: (value: number | string) => {
                if (!question.required) {
                  if (value === '' || value === undefined || value === null || Number.isNaN(value as number)) {
                    return true;
                  }
                } else {
                  if (value === '' || value === undefined || value === null || Number.isNaN(value as number)) {
                    return isAr ? 'مطلوب' : 'Required';
                  }
                }
                if (hasRegex && value !== '' && value !== undefined && value !== null) {
                  try {
                    return regexPattern!.test(String(value)) || regexMsg;
                  } catch {
                    return true;
                  }
                }
                return true;
              },
            })}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            placeholder={placeholder || ''}
            disabled={disabled}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: requiredMsg,
              ...(hasRegex ? { pattern: { value: regexPattern!, message: regexMsg } } : {}),
            })}
          />
        );

      case 'time':
        return (
          <Input
            type="time"
            placeholder={placeholder || ''}
            disabled={disabled}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: requiredMsg,
              ...(hasRegex ? { pattern: { value: regexPattern!, message: regexMsg } } : {}),
            })}
          />
        );

      case 'textarea':
        return (
          <Textarea
            placeholder={placeholder || ''}
            disabled={disabled}
            rows={4}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: requiredMsg,
              ...(hasRegex ? { pattern: { value: regexPattern!, message: regexMsg } } : {}),
            })}
          />
        );

      case 'single_choice': {
        // Model-backed dropdown: render the searchable combobox so 4000+
        // rows don't ship to the client. The component owns its own data
        // fetching (search + cascade) — we just plumb in the dependency
        // answers and persist the chosen label across fetches.
        if (isDynamic && question.option_source) {
          return (
            <Controller
              name={fieldName}
              control={form.control}
              rules={{
                validate: (v: string) => {
                  if (!question.required) return true;
                  if (v !== undefined && v !== null && String(v).trim() !== '') return true;
                  return isAr ? 'مطلوب' : 'Required';
                },
              }}
              render={({ field }) => (
                <SearchableDynamicDropdown
                  value={field.value || ''}
                  onChange={field.onChange}
                  snapshotId={snapshotId}
                  questionCode={question.code}
                  optionSource={question.option_source!}
                  dependencyAnswers={dependencyAnswers}
                  language={isAr ? 'ar' : 'en'}
                  placeholder={placeholder || (isAr ? 'اختر...' : 'Select...')}
                  disabled={disabled}
                  hasError={!!errorMessage}
                  selectedLabel={selectedDynamicLabel}
                  onSelectedLabelChange={setSelectedDynamicLabel}
                />
              )}
            />
          );
        }

        // Static options: Select for many, RadioGroup for few.
        if (question.options.length > 5) {
          return (
            <Controller
              name={fieldName}
              control={form.control}
              rules={{
                validate: (v: string) => {
                  if (!question.required) return true;
                  if (v !== undefined && v !== null && String(v).trim() !== '') return true;
                  return isAr ? 'مطلوب' : 'Required';
                },
              }}
              render={({ field }) => (
                <Select
                  disabled={disabled}
                  value={field.value || ''}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                >
                  <SelectTrigger className={errorMessage ? 'border-destructive' : ''}>
                    <SelectValue placeholder={placeholder || (isAr ? 'اختر...' : 'Select...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {getOptionLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          );
        }

        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: string) => {
                if (!question.required) return true;
                if (v !== undefined && v !== null && String(v).trim() !== '') return true;
                return isAr ? 'مطلوب' : 'Required';
              },
            }}
            render={({ field }) => (
              <RadioGroup
                disabled={disabled}
                value={field.value || ''}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
                className="space-y-2"
              >
                {question.options.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2 rtl:space-x-reverse">
                    <RadioGroupItem value={option.value} id={`${fieldName}-${option.value}`} />
                    <Label
                      htmlFor={`${fieldName}-${option.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {getOptionLabel(option)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        );
      }

      case 'multi_choice':
      case 'multiple_choice':
        if (isDynamic && question.option_source) {
          return (
            <Controller
              name={fieldName}
              control={form.control}
              rules={{
                validate: (v: string[] | undefined) => {
                  // Cascading dropdowns (e.g. neighborhood) are required only when
                  // the chosen parent actually has options; others honor question.required.
                  const required = isCascadingDynamic ? hasOptionsRef.current : question.required;
                  if (!required) return true;
                  const arr = Array.isArray(v) ? v : [];
                  if (arr.length > 0) return true;
                  return isAr ? 'مطلوب' : 'Required';
                },
              }}
              render={({ field }) => {
                const currentValues: string[] = Array.isArray(field.value)
                  ? field.value
                  : [];
                return (
                  <SearchableDynamicDropdown
                    multiple
                    value={currentValues}
                    onChange={field.onChange}
                    snapshotId={snapshotId}
                    questionCode={question.code}
                    optionSource={question.option_source!}
                    dependencyAnswers={dependencyAnswers}
                    language={isAr ? 'ar' : 'en'}
                    placeholder={placeholder || (isAr ? 'اختر...' : 'Select...')}
                    disabled={disabled}
                    hasError={!!errorMessage}
                    selectedLabel={selectedDynamicLabels}
                    onSelectedLabelChange={setSelectedDynamicLabels}
                    onAvailabilityChange={isCascadingDynamic ? setHasOptions : undefined}
                  />
                );
              }}
            />
          );
        }
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: string[] | undefined) => {
                if (!question.required) return true;
                const arr = Array.isArray(v) ? v : [];
                if (arr.length > 0) return true;
                return isAr ? 'مطلوب' : 'Required';
              },
            }}
            render={({ field }) => {
              const currentValues: string[] = field.value || [];
              return (
                <div className="space-y-2">
                  {question.options.map((option) => {
                    const isChecked = currentValues.includes(option.value);
                    return (
                      <div key={option.value} className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Checkbox
                          id={`${fieldName}-${option.value}`}
                          checked={isChecked}
                          disabled={disabled}
                          onCheckedChange={(checked) => {
                            const newValues = checked
                              ? [...currentValues, option.value]
                              : currentValues.filter((x: string) => x !== option.value);
                            field.onChange(newValues);
                          }}
                        />
                        <Label
                          htmlFor={`${fieldName}-${option.value}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {getOptionLabel(option)}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        );

      case 'yes_no': {
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: unknown) => {
                if (!question.required) return true;
                if (v === undefined || v === null || v === '') {
                  return isAr ? 'مطلوب' : 'Required';
                }
                return true;
              },
            }}
            render={({ field }) => {
              const yesNoValue = field.value;
              const isYes =
                yesNoValue === 'yes' ||
                yesNoValue === true ||
                (yesNoValue && typeof yesNoValue === 'object' && (yesNoValue as { choice?: string }).choice === 'yes');
              const isNo = yesNoValue === 'no' || yesNoValue === false;
              return (
                <div className="flex gap-3">
                  {([
                    { val: 'yes' as const, label: isAr ? 'نعم' : 'Yes', selected: isYes },
                    { val: 'no' as const, label: isAr ? 'لا' : 'No', selected: isNo },
                  ]).map((opt) => (
                    <button
                      key={opt.val}
                      type="button"
                      disabled={disabled}
                      onClick={() => field.onChange(opt.val)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg transition-all text-sm font-medium
                  ${opt.selected
                    ? 'bg-primary/5 border-primary ring-1 ring-primary/20 text-primary'
                    : 'border-input hover:bg-accent/50 hover:border-primary/30 text-foreground'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors
                    ${opt.selected ? 'border-primary bg-primary' : 'border-muted-foreground/30'}
                  `}
                      >
                        {opt.selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      {opt.label}
                    </button>
                  ))}
                </div>
              );
            }}
          />
        );
      }

      case 'yes_no_conditional': {
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: unknown) => {
                if (!question.required) return true;
                if (v === undefined || v === null || v === '') {
                  return isAr ? 'مطلوب' : 'Required';
                }
                const pickedYes = v === 'yes' || v === true;
                const pickedYesWithDetails =
                  v &&
                  typeof v === 'object' &&
                  'choice' in v &&
                  (v as { choice: string }).choice === 'yes';
                if (pickedYes) {
                  return isAr ? 'أدخل التفاصيل' : 'Please enter details';
                }
                if (pickedYesWithDetails) {
                  const d = String((v as { details?: string }).details || '').trim();
                  if (!d) return isAr ? 'أدخل التفاصيل' : 'Please enter details';
                }
                return true;
              },
            }}
            render={({ field }) => {
              const yesNoValue = field.value;
              const isYes =
                yesNoValue === 'yes' ||
                yesNoValue === true ||
                (yesNoValue && typeof yesNoValue === 'object' && (yesNoValue as { choice?: string }).choice === 'yes');
              const isNo = yesNoValue === 'no' || yesNoValue === false;
              const currentDetails =
                yesNoValue && typeof yesNoValue === 'object' ? ((yesNoValue as { details?: string }).details || '') : '';

              const setYesNoConditional = (choice: 'yes' | 'no', details?: string) => {
                if (choice === 'no') {
                  field.onChange('no');
                } else {
                  field.onChange(
                    details !== undefined ? { choice: 'yes', details } : 'yes',
                  );
                }
              };

              return (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {([
                      { val: 'yes' as const, label: isAr ? 'نعم' : 'Yes', selected: isYes },
                      { val: 'no' as const, label: isAr ? 'لا' : 'No', selected: isNo },
                    ]).map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          setYesNoConditional(opt.val, opt.val === 'yes' ? currentDetails : undefined)
                        }
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg transition-all text-sm font-medium
                    ${opt.selected
                      ? 'bg-primary/5 border-primary ring-1 ring-primary/20 text-primary'
                      : 'border-input hover:bg-accent/50 hover:border-primary/30 text-foreground'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                      >
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors
                      ${opt.selected ? 'border-primary bg-primary' : 'border-muted-foreground/30'}
                    `}
                        >
                          {opt.selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {isYes && (
                    <div className="rounded-lg border border-border bg-muted/30 p-3 animate-in fade-in-50 slide-in-from-top-1">
                      <Label className="text-sm font-medium text-muted-foreground mb-2 block">
                        {isAr ? 'يرجى وصف التفاصيل' : 'Please describe the details'}
                      </Label>
                      <Textarea
                        placeholder={isAr ? 'اكتب التفاصيل هنا...' : 'Write details here...'}
                        disabled={disabled}
                        rows={3}
                        value={currentDetails}
                        onChange={(e) => setYesNoConditional('yes', e.target.value)}
                        className="bg-background"
                      />
                    </div>
                  )}
                </div>
              );
            }}
          />
        );
      }

      case 'boolean':
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: boolean | undefined) => {
                if (!question.required) return true;
                if (v === true) return true;
                return isAr ? 'مطلوب' : 'Required';
              },
            }}
            render={({ field }) => (
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <Checkbox
                  id={fieldName}
                  checked={field.value || false}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true);
                  }}
                />
                <Label htmlFor={fieldName} className="text-sm font-normal cursor-pointer">
                  {questionText}
                </Label>
              </div>
            )}
          />
        );

      case 'file': {
        const allowMultiple =
          question.multiple === true ||
          question.config?.multiple === true ||
          (typeof question.validation_rules?.max_files === 'number' &&
            question.validation_rules.max_files > 1);
        const fileRequiredMsg = isAr ? 'يرفع الملف مطلوب' : 'File upload is required';
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: string | string[] | undefined) => {
                if (!question.required) return true;
                if (allowMultiple) {
                  const ok = Array.isArray(v) && v.length > 0 && v.every((id) => typeof id === 'string' && id.length > 0);
                  return ok || fileRequiredMsg;
                }
                const ok = typeof v === 'string' && v.length > 0;
                return ok || fileRequiredMsg;
              },
            }}
            render={({ field }) => (
              <FileUploadField
                questionCode={question.code}
                multiple={allowMultiple}
                accept={
                  question.validation_rules?.file_types
                    ? (question.validation_rules.file_types as string[]).join(',')
                    : undefined
                }
                maxSizeMB={(question.validation_rules?.max_size_mb as number) || 10}
                value={field.value}
                onChange={field.onChange}
                locale={locale}
                disabled={disabled}
              />
            )}
          />
        );
      }

      case 'multi_file':
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              validate: (v: string | string[] | undefined) => {
                if (!question.required) return true;
                const ok = Array.isArray(v) && v.length > 0 && v.every((id) => typeof id === 'string' && id.length > 0);
                return ok || (isAr ? 'يرفع الملف مطلوب' : 'File upload is required');
              },
            }}
            render={({ field }) => (
              <FileUploadField
                questionCode={question.code}
                multiple={true}
                accept={
                  question.validation_rules?.file_types
                    ? (question.validation_rules.file_types as string[]).join(',')
                    : undefined
                }
                maxSizeMB={(question.validation_rules?.max_size_mb as number) || 10}
                value={field.value}
                onChange={field.onChange}
                locale={locale}
                disabled={disabled}
              />
            )}
          />
        );

      case 'formset':
        return (
          <Controller
            name={fieldName}
            control={form.control}
            rules={{
              required: question.required
                ? (isAr ? 'يجب تحديد الأقل يوم واحد متاح' : 'At least one day must be set as available')
                : false,
              validate: (val: WeeklyScheduleSlot[] | undefined) => {
                if (!question.required) return true;
                const slots = Array.isArray(val) ? val : [];
                const hasAvailable = slots.some(
                  (s) => s.available === true && (s.start_time || s.end_time)
                );
                if (!hasAvailable && question.required) {
                  return isAr ? 'حدد الأقل يوم واحد مع أوقات العمل' : 'Set at least one day with working hours';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <FormsetField
                questionCode={question.code}
                value={field.value}
                onChange={field.onChange}
                locale={locale}
                disabled={disabled}
                required={question.required}
                hasError={!!errorMessage}
              />
            )}
          />
        );

      default:
        // Fallback to text input for unknown types
        return (
          <Input
            type="text"
            placeholder={placeholder || ''}
            disabled={disabled}
            className={errorMessage ? 'border-destructive' : ''}
            {...form.register(fieldName, {
              required: requiredMsg,
              ...(hasRegex ? { pattern: { value: regexPattern!, message: regexMsg } } : {}),
            })}
          />
        );
    }
  };

  // For boolean type, the label is rendered inline with the checkbox
  if (question.type === 'boolean') {
    return (
      <div className="space-y-1">
        {renderField()}
        {helpText && (
          <p className="text-xs text-muted-foreground">{helpText}</p>
        )}
        {formatHint && !errorMessage && (
          <p className="text-xs text-muted-foreground">{formatHint}</p>
        )}
        {errorMessage && (
          <p className="text-sm font-medium text-destructive">{errorMessage}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label
        htmlFor={fieldName}
        className={`text-sm font-medium ${errorMessage ? 'text-destructive' : ''}`}
      >
        {questionText}
        {(isCascadingDynamic ? hasOptions : question.required) && (
          <span className="text-destructive ms-1">*</span>
        )}
      </Label>
      {helpText && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {renderField()}
      {formatHint && !errorMessage && (
        <p className="text-xs text-muted-foreground">{formatHint}</p>
      )}
      {errorMessage && (
        <p className="text-sm font-medium text-destructive">{errorMessage}</p>
      )}
    </div>
  );
};
